import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import TrendingUpIcons from '@/icons/TrendingUpIcons'
import { formatCurrency } from '@/utils/function'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts'
import { Typogaphy } from 'vnetwork-ui'
import {
    getRevenueByAssignee,
    RevenueByAssigneePayload,
} from '../../../services'
import { SmallChartSkeleton } from './skeletons'

interface RevenueByAssigneeChartProps {
    filterPayload: RevenueByAssigneePayload
}

// Colors for pie chart segments
const COLORS = [
    '#3b82f6', // blue
    '#22c55e', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // violet
    '#06b6d4', // cyan
    '#ec4899', // pink
    '#f97316', // orange
    '#14b8a6', // teal
    '#6366f1', // indigo
]

const RevenueByAssigneeChart: React.FC<RevenueByAssigneeChartProps> = ({
    filterPayload,
}) => {
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.MARKETING,
                'revenue-by-assignee',
                filterPayload,
            ],
            queryFn: () => getRevenueByAssignee(filterPayload),
        },
        { loadingComponent: <SmallChartSkeleton /> }
    )

    const stats = data?.results || []
    const totalRevenue = data?.total_revenue || 0

    // Filter out entries with 0 revenue and sort by revenue desc
    const chartData = useMemo(() => {
        return stats
            .filter((item) => item.total_revenue > 0)
            .sort((a, b) => b.total_revenue - a.total_revenue)
            .map((item) => ({
                name: item.employee_name,
                value: item.total_revenue,
                leads: item.total_leads,
                converted: item.converted_leads,
            }))
    }, [stats])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    // Custom tooltip
    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload
            const percentage =
                totalRevenue > 0
                    ? ((data.value / totalRevenue) * 100).toFixed(1)
                    : 0
            return (
                <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                    <p className="font-medium text-gray-800 mb-1">
                        {data.name}
                    </p>
                    <p className="text-sm text-green-600 font-semibold">
                        {formatCurrency(data.value)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        {percentage}% {t('of-total')}
                    </p>
                    <div className="text-xs text-gray-400 mt-1 pt-1 border-t">
                        <span>{data.leads} leads</span>
                        <span className="mx-1">•</span>
                        <span>{data.converted} converted</span>
                    </div>
                </div>
            )
        }
        return null
    }

    // Custom legend
    const renderLegend = (props: any) => {
        const { payload } = props
        return (
            <div className="flex flex-wrap justify-center gap-2 mt-2">
                {payload.map((entry: any, index: number) => (
                    <div
                        key={`legend-${index}`}
                        className="flex items-center gap-1 text-xs"
                    >
                        <div
                            className="w-3 h-3 rounded-sm"
                            style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-gray-600 truncate max-w-[100px]">
                            {entry.value}
                        </span>
                    </div>
                ))}
            </div>
        )
    }

    return (
        <div className="bg-white rounded-md border border-gray-200 p-4 h-full flex flex-col">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <TrendingUpIcons className="text-primary w-4 h-4" />
                    <Typogaphy className="font-medium text-primary capitalize">
                        {t('revenue-by-assignee')}
                    </Typogaphy>
                </div>
                <div className="flex items-center gap-2 px-3">
                    <Typogaphy className="text-xs capitalize text-primary font-medium">
                        {t('total')}:
                    </Typogaphy>
                    <Typogaphy className="text-xs font-medium text-primary">
                        {totalRevenue > 0
                            ? formatCurrency(totalRevenue)
                            : '---'}
                    </Typogaphy>
                </div>
            </div>

            {chartData.length === 0 ? (
                <div className="flex items-center justify-center flex-1 text-gray-400 text-sm">
                    {t('no-data')}
                </div>
            ) : (
                <div className="flex-1 min-h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={2}
                                dataKey="value"
                                label={({
                                    percent,
                                    ...props
                                }: {
                                    percent?: number
                                    [key: string]: any
                                }) => (
                                    <text
                                        x={props.x}
                                        y={props.y}
                                        textAnchor={props.textAnchor}
                                        dominantBaseline="central"
                                        fontSize={11}
                                    >
                                        {`${((percent ?? 0) * 100).toFixed(0)}%`}
                                    </text>
                                )}
                                labelLine={false}
                            >
                                {chartData.map((_, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={COLORS[index % COLORS.length]}
                                    />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend content={renderLegend} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    )
}

export default RevenueByAssigneeChart
